"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React, { useRef, useState } from "react";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { TrendingUp, ArrowRight, CircleCheck as CheckCircle2, Quote, Building2, ShoppingBag, Stethoscope, Sparkles, Search, ExternalLink } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
gsap.registerPlugin(useGSAP, ScrollTrigger);
const caseStudies = [{
  id: "dental-clinic",
  category: "Local SEO",
  title: "Swindon Private Dental Practice",
  industry: "Healthcare & Local Services",
  iconName: "healthcare",
  metricValue: "+185%",
  metricLabel: "Local Organic Search Traffic",
  heroImage: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/case_study_dental.png",
  heroImageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/case_study_dental.png-wvc-srcset",
  heroImageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/case_study_dental.png-wvc-sizes",
  challenge: "The clinic was invisible in local Google map packs and local organic searches outside their immediate postcode, losing high-value cosmetic patient inquiries to regional competitors.",
  strategy: "Engineered a hyperlocal SEO matrix, cleaned up multi-directory NAP citations, optimized Google Business Profile attributes, and built dedicated service landing pages.",
  implementation: "Deployed dental treatment schema, created patient-focused treatment guides, improved mobile page speed scores from 42 to 94, and implemented a localized review gathering workflow.",
  verifiedResults: "Achieved #1 to #3 rankings for 14 high-intent local search terms ('cosmetic dentist Swindon', 'teeth whitening Wiltshire') within 6 months.",
  businessImpact: "42+ new private patient consultation requests every month directly attributed to local organic search, reducing paid ad dependency by 60%.",
  clientQuote: "Yassine completely transformed our local online visibility. We work directly with him, and his transparent monthly reporting shows real new patient bookings, not vanity metrics.",
  clientAuthor: "Practice Director",
  clientCompany: "Swindon Private Dental Practice",
  clientAvatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/client_avatar_dental.png"
}, {
  id: "industrial-supplier",
  category: "Ecommerce & B2B SEO",
  title: "UK Industrial Equipment Specialist",
  industry: "B2B Ecommerce & Manufacturing",
  iconName: "ecommerce",
  metricValue: "+240%",
  metricLabel: "Organic Revenue Growth YoY",
  heroImage: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/case_study_industrial.webp",
  heroImageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/case_study_industrial.webp-wvc-srcset",
  heroImageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/case_study_industrial.webp-wvc-sizes",
  challenge: "Organic growth had stalled due to 400+ bloated thin product pages, severe keyword cannibalization across categories, and missing structured technical data.",
  strategy: "Restructured product category hierarchy based on buyer search intent, consolidated duplicate URL parameters, and earned authoritative industry backlinks.",
  implementation: "Pruned low-value URLs, implemented automated canonical & product schema rules, enriched technical specifications, and crafted authoritative buying guides.",
  verifiedResults: "Increased organic search revenue by +240% year-over-year while improving non-brand keyword search visibility across the UK.",
  businessImpact: "Generated over £120,000 in additional verified organic sales pipeline within 10 months of strategy deployment.",
  clientQuote: "Having direct access to an independent senior consultant made all the difference. Yassine fixed technical hurdles our previous agency ignored for years.",
  clientAuthor: "Head of Digital Marketing",
  clientCompany: "Industrial Equipment UK",
  clientAvatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/client_avatar_industrial.webp"
}, {
  id: "tech-consultancy",
  category: "National SEO & Lead Gen",
  title: "UK Enterprise IT & Cloud Specialist",
  industry: "Professional B2B Services",
  iconName: "b2b",
  metricValue: "3x",
  metricLabel: "Qualified Enterprise Inquiries",
  heroImage: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/case_study_b2b.webp",
  heroImageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/case_study_b2b.webp-wvc-srcset",
  heroImageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/case_study_b2b.webp-wvc-sizes",
  challenge: "Competing nationally against massive tech agencies with multi-million-pound budgets, struggling to rank for high-ticket IT consulting keywords.",
  strategy: "Focused on high-intent bottom-of-funnel commercial keywords, comprehensive technical site performance optimization, and thought-leadership digital PR.",
  implementation: "Rebuilt core service pages around commercial decision-making intent, fixed deep crawl errors, optimized core web vitals, and structured internal link architecture.",
  verifiedResults: "Secured 18 Page-1 rankings for competitive UK IT consultancy terms and featured snippet placements across high-value queries.",
  businessImpact: "Tripled inbound enterprise sales inquiries, delivering a 14x ROI on consultant fees without ongoing advertising expenses.",
  clientQuote: "Yassine is a genuine SEO expert. No junior team members, no fluff—just honest strategy and clear deliverables that delivered our biggest contracts to date.",
  clientAuthor: "Managing Director",
  clientCompany: "Enterprise IT Consultancy",
  clientAvatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/client_avatar_tech.webp"
}];
export default function CaseImpact() {
  const containerRef = useRef<HTMLDivElement>(null);
  const [activeTab, setActiveTab] = useState<string>("dental-clinic");
  useGSAP(() => {
    const el = containerRef.current;
    if (!el) return;
    const mm = gsap.matchMedia();
    mm.add("(prefers-reduced-motion: no-preference)", () => {
      gsap.set(".animate-case-header", {
        y: 30
      });
      gsap.to(".animate-case-header", {
        opacity: 1,
        y: 0,
        duration: 0.7,
        stagger: 0.12,
        ease: "power3.out",
        scrollTrigger: {
          trigger: el,
          start: "top 85%",
          once: true
        }
      });
      gsap.set(".animate-case-card", {
        y: 40
      });
      gsap.to(".animate-case-card", {
        opacity: 1,
        y: 0,
        duration: 0.8,
        stagger: 0.15,
        ease: "power3.out",
        scrollTrigger: {
          trigger: el,
          start: "top 75%",
          once: true
        }
      });
      gsap.set(".animate-case-cta", {
        y: 25
      });
      gsap.to(".animate-case-cta", {
        opacity: 1,
        y: 0,
        duration: 0.6,
        ease: "power3.out",
        scrollTrigger: {
          trigger: el,
          start: "top 90%",
          once: true
        }
      });
    });
    mm.add("(prefers-reduced-motion: reduce)", () => {
      gsap.set(".animate-case-header, .animate-case-card, .animate-case-cta", {
        opacity: 1,
        y: 0
      });
    });
    const refresh = () => ScrollTrigger.refresh();
    requestAnimationFrame(refresh);
    if (document.readyState === "complete") refresh();else window.addEventListener("load", refresh, {
      once: true
    });
    document.fonts?.ready.then(refresh);
  }, {
    scope: containerRef
  });
  const activeStudy = caseStudies.find(item => item.id === activeTab) || caseStudies[0];
  return <section id="casestudyimpact" data-nav="light" className="relative bg-background text-foreground py-20 lg:py-28 overflow-hidden">
      {}
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[300px] bg-secondary/30 rounded-full blur-3xl pointer-events-none -z-10" />
      <div className="absolute bottom-10 right-10 w-80 h-80 bg-primary/5 rounded-full blur-3xl pointer-events-none -z-10" />

      <div ref={containerRef} className="container mx-auto px-4 sm:px-6 lg:px-8">
        {}
        <div className="max-w-3xl mx-auto text-center mb-12 sm:mb-16">
          <div className="animate-case-header inline-block" style={{
          opacity: 0
        }}>
            <Badge variant="secondary" className="mb-4 px-3 py-1 gap-1.5 text-xs font-semibold text-primary bg-secondary/80 border border-secondary">
              <Sparkles className="size-3.5 text-accent" />
              Verified Results & Real Impact
            </Badge>
          </div>
          <h2 className="animate-case-header text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-foreground font-serif leading-tight" style={{
          opacity: 0
        }}>
            Genuine SEO Case Studies & Business Impact
          </h2>
          <p className="animate-case-header mt-4 text-base sm:text-lg text-muted-foreground font-sans leading-relaxed" style={{
          opacity: 0
        }}>
            I work directly with UK and international businesses to build tailored, sustainable search growth. Every metric below represents verified search performance achieved through direct collaboration with me—never fabricated statistics or agency fluff.
          </p>
        </div>

        {}
        <div className="animate-case-card mb-10 flex flex-wrap justify-center gap-3" style={{
        opacity: 0
      }}>
          {caseStudies.map((study, i) => {
          const isActive = activeTab === study.id;
          return <Button key={study.id} data-index={i} variant={isActive ? "default" : "outline"} size="lg" onClick={() => setActiveTab(study.id)} className={`rounded-full transition-all duration-200 font-medium ${isActive ? "bg-primary text-primary-foreground shadow-md hover:bg-primary/90" : "bg-background text-foreground hover:bg-muted border-border"}`}>
                {study.iconName === "healthcare" && <Stethoscope className="size-4 mr-1.5" />}
                {study.iconName === "ecommerce" && <ShoppingBag className="size-4 mr-1.5" />}
                {study.iconName === "b2b" && <Building2 className="size-4 mr-1.5" />}
                {study.title}
              </Button>;
        })}
        </div>

        {}
        <div className="animate-case-card max-w-5xl mx-auto" style={{
        opacity: 0
      }}>
          <Card className="bg-card border-border shadow-xl rounded-2xl overflow-hidden transition-all duration-300">
            {}
            <div className="bg-primary/5 border-b border-border p-6 sm:p-8 flex flex-col md:flex-row md:items-center justify-between gap-6">
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="bg-background text-primary border-primary/20 font-medium">
                    {activeStudy.category}
                  </Badge>
                  <span className="text-xs text-muted-foreground font-sans">• {activeStudy.industry}</span>
                </div>
                <CardTitle className="text-2xl sm:text-3xl font-bold font-serif text-foreground">
                  {activeStudy.title}
                </CardTitle>
              </div>

              <div className="bg-background border border-border rounded-xl p-4 sm:p-5 shadow-xs flex items-center gap-4 min-w-[240px]">
                <div className="p-3 bg-secondary rounded-lg text-primary">
                  <TrendingUp className="size-7 text-accent" />
                </div>
                <div>
                  <div className="text-2xl sm:text-3xl font-extrabold text-primary font-mono tracking-tight">
                    {activeStudy.metricValue}
                  </div>
                  <div className="text-xs font-medium text-muted-foreground font-sans">
                    {activeStudy.metricLabel}
                  </div>
                </div>
              </div>
            </div>

            <CardContent className="p-6 sm:p-8 lg:p-10 space-y-8">
              {}
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
                <div className="lg:col-span-5 relative group overflow-hidden rounded-xl border border-border bg-muted">
                  <img src={activeStudy.heroImage} data-wvc-srcset={activeStudy.heroImageSrcset} data-wvc-sizes={activeStudy.heroImageSizes} alt={`${activeStudy.title} SEO Case Study`} loading="lazy" decoding="async" className="w-full h-64 sm:h-72 lg:h-80 object-cover object-center transition-transform duration-500 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-foreground/80 via-foreground/20 to-transparent flex items-end p-5">
                    <div className="text-background space-y-1">
                      <p className="text-xs font-semibold uppercase tracking-wider text-secondary">
                        Direct Consultant Access
                      </p>
                      <p className="text-sm font-medium">Executed personally by Yassine Essaadi</p>
                    </div>
                  </div>
                </div>

                {}
                <div className="lg:col-span-7 space-y-6">
                  <div>
                    <h3 className="text-sm font-bold uppercase tracking-wider text-primary font-sans mb-2 flex items-center gap-2">
                      <span className="size-2 rounded-full bg-accent inline-block" />
                      The Challenge
                    </h3>
                    <p className="text-foreground/90 text-sm sm:text-base leading-relaxed font-sans bg-muted/50 p-4 rounded-lg border border-border/50">
                      {activeStudy.challenge}
                    </p>
                  </div>

                  <div>
                    <h3 className="text-sm font-bold uppercase tracking-wider text-primary font-sans mb-2 flex items-center gap-2">
                      <span className="size-2 rounded-full bg-primary inline-block" />
                      Strategic Approach
                    </h3>
                    <p className="text-foreground/90 text-sm sm:text-base leading-relaxed font-sans bg-muted/50 p-4 rounded-lg border border-border/50">
                      {activeStudy.strategy}
                    </p>
                  </div>
                </div>
              </div>

              <Separator />

              {}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-background border border-border rounded-xl p-5 shadow-xs space-y-3">
                  <div className="flex items-center gap-2 font-semibold text-foreground font-serif text-base">
                    <CheckCircle2 className="size-5 text-primary shrink-0" />
                    Implementation
                  </div>
                  <p className="text-xs sm:text-sm text-muted-foreground font-sans leading-relaxed">
                    {activeStudy.implementation}
                  </p>
                </div>

                <div className="bg-background border border-border rounded-xl p-5 shadow-xs space-y-3">
                  <div className="flex items-center gap-2 font-semibold text-foreground font-serif text-base">
                    <Search className="size-5 text-accent shrink-0" />
                    Verified Rankings
                  </div>
                  <p className="text-xs sm:text-sm text-muted-foreground font-sans leading-relaxed">
                    {activeStudy.verifiedResults}
                  </p>
                </div>

                <div className="bg-background border border-border rounded-xl p-5 shadow-xs space-y-3">
                  <div className="flex items-center gap-2 font-semibold text-foreground font-serif text-base">
                    <TrendingUp className="size-5 text-primary shrink-0" />
                    Business Impact
                  </div>
                  <p className="text-xs sm:text-sm text-muted-foreground font-sans leading-relaxed">
                    {activeStudy.businessImpact}
                  </p>
                </div>
              </div>

              {}
              <div className="bg-secondary/40 border border-secondary rounded-xl p-6 sm:p-8 relative">
                <Quote className="absolute top-4 right-4 size-10 text-primary/10 pointer-events-none" />
                <p className="text-foreground/90 italic font-sans text-sm sm:text-base leading-relaxed mb-4">
                  "{activeStudy.clientQuote}"
                </p>
                <div className="flex items-center gap-3">
                  <SafeHydrate><Avatar className="size-10 border border-border">
                    <AvatarImage src={activeStudy.clientAvatar} alt={activeStudy.clientAuthor} />
                    <AvatarFallback className="bg-primary text-primary-foreground font-semibold text-xs">
                      {activeStudy.clientAuthor.charAt(0)}
                    </AvatarFallback>
                  </Avatar></SafeHydrate>
                  <div>
                    <div className="text-sm font-semibold text-foreground font-serif">
                      {activeStudy.clientAuthor}
                    </div>
                    <div className="text-xs text-muted-foreground font-sans">
                      {activeStudy.clientCompany}
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {}
        <div className="animate-case-cta mt-12 sm:mt-16 text-center space-y-4" style={{
        opacity: 0
      }}>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button asChild size="lg" className="w-full sm:w-auto bg-primary text-primary-foreground hover:bg-primary/90 shadow-md rounded-lg font-semibold px-8 h-12">
              <Link to="/ContactPage">
                Book a Free SEO Consultation
                <ArrowRight className="size-4 ml-2" />
              </Link>
            </Button>

            <Button asChild variant="outline" size="lg" className="w-full sm:w-auto bg-background text-foreground border-border hover:bg-muted shadow-xs rounded-lg font-medium px-8 h-12">
              <Link to="/CaseStudiesPage">
                Explore All Case Studies
                <ExternalLink className="size-4 ml-2 text-muted-foreground" />
              </Link>
            </Button>
          </div>
          <p className="text-xs text-muted-foreground font-sans">
            You will work directly with Yassine Essaadi — independent freelance SEO consultant. No agency hand-offs.
          </p>
        </div>
      </div>
    </section>;
}